import { AccessLevel } from "@/api/williams/users";
import { areAnyTestsCompleted, doAllWeldersHaveTest, doesWelderHaveTest } from "../lookup/utils";
import { ConfirmationDialog } from "@/components/dialogs";
import { createTestResult, isTestCompleted } from "../lookup/utils";
import { downloadCertificate } from "@/api/williams/certificates";
import { faSearch, faXmark } from "@fortawesome/free-solid-svg-icons";
import { faSquareCheck, faSquareMinus } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconButton } from "../../components";
import { StandardTest } from "@/api/williams/tests";
import { String } from "@/util";
import { TEFormWeldersMobile } from "./TEFormWeldersMobile";
import { TestEvent, TestEventStatus, WelderTest } from "@/api/williams/testEvents";
import { useFormContext } from "react-hook-form";
import { useModalDialog } from "@/hooks";
import { usePermissions } from "../../users/hooks";
import { Welder } from "@/api/williams/welders";
import { WelderSelectionDialog } from "../../welders/dialogs";

export function TEFormWelders({ data }: Readonly<{ data: TestEvent }>) {
  const form = useFormContext<TestEvent>();
  const dialog = useModalDialog();
  const permissions = usePermissions();
  const role = permissions.data?.accessLevel;

  function setTestResults(results: WelderTest[]) {
    form.setValue("welderTests", results, { shouldDirty: true });
  }

  function handleSelectWelders() {
    if (data.status < TestEventStatus.Initiated) {
      // Trigger extended form validation.
      form.setValue("status", TestEventStatus.Initiated);
    }

    return form.handleSubmit(selectWelders)();
  }

  function selectWelders() {
    dialog.show(WelderSelectionDialog, {
      initialWelders: data.welders,
      onAccept: (selectedWelders) => {
        form.setValue("welders", selectedWelders, { shouldDirty: true });
      },
    });
  }

  function handleTestSelectionChanged(test: StandardTest, welder: Welder, selected: boolean) {
    if (selected) {
      setTestResults([...data.welderTests, createTestResult(data, welder, test)]);
    } else {
      setTestResults(
        data.welderTests.filter((tw) => !(tw.templateId === test.recordId && tw.welderId === welder.welderId))
      );
    }
  }

  function handleSelectAll() {
    const results = [...data.welderTests];

    for (const welder of data.welders) {
      for (const test of data.tests) {
        if (!doesWelderHaveTest(data, welder, test)) {
          results.push(createTestResult(data, welder, test));
        }
      }
    }

    setTestResults(results);
  }

  function handleClearAll() {
    // Only completed tests will remain selected.
    setTestResults(data.welderTests.filter((x) => x.submitted));
  }

  function handleSelectByColumn(test: StandardTest, selected: boolean) {
    const results = [...data.welderTests];

    if (selected) {
      for (const welder of data.welders) {
        if (!doesWelderHaveTest(data, welder, test)) {
          results.push(createTestResult(data, welder, test));
        }
      }

      setTestResults(results);
    } else {
      setTestResults(results.filter((x) => x.templateId !== test.recordId || x.submitted));
    }
  }

  function handleRemoveWelder(welder: Welder) {
    const welders = data.welders.filter((x) => x.welderId !== welder.welderId);

    dialog.show(ConfirmationDialog, {
      caption: "Remove Welder",
      message: `Are you sure you want to remove ${welder.name}?`,
      onAccept: () => {
        form.setValue("welders", welders, { shouldDirty: true });
      },
    });
  }

  return (
    <div className="mt-4">
      <hr />
      <div className="d-flex justify-content-between">
        <h3>Welders</h3>
        {role !== AccessLevel.GeneralUser && (
          <div className="d-flex gap-2 my-auto">
            {data.welders.length > 0 && data.tests.length > 0 && (
              <>
                <IconButton icon={faSquareCheck} title="Select All" onClick={handleSelectAll} disabled={data.locked} />
                <IconButton icon={faSquareMinus} title="Clear All" onClick={handleClearAll} disabled={data.locked} />
              </>
            )}
            <IconButton icon={faSearch} title="Search" onClick={handleSelectWelders} disabled={data.locked} />
          </div>
        )}
      </div>

      <div className="d-xl-none d-block mt-3">
        <TEFormWeldersMobile
          data={data}
          handleRemoveWelder={handleRemoveWelder}
          handleTestSelectionChanged={handleTestSelectionChanged}
        />
      </div>

      {data.welders && data.welders.length ? (
        <table className="table table-fixed align-middle d-none d-xl-table mt-3">
          <thead className="table-light align-middle">
            <tr>
              <th className="col-auto">Welder</th>
              {data.tests.map((test) => (
                <th key={test.recordId} className="col text-center text-nowrap">
                  <a title={test.templateNumber} onClick={() => downloadCertificate(test.recordId)}>
                    {String.truncate(test.templateNumber, 70 / data.tests.length)}
                  </a>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.tests.length > 0 && data.welders.length > 1 && (
              <tr>
                <td />
                {data.tests.map((test) => (
                  <td key={test.recordId} className="text-center">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={doAllWeldersHaveTest(data, test)}
                      onChange={(e) => handleSelectByColumn(test, e.currentTarget.checked)}
                      disabled={data.locked || role === AccessLevel.GeneralUser}
                    />
                  </td>
                ))}
              </tr>
            )}
            {data.welders.map((welder) => (
              <tr key={welder.welderId}>
                <td className="ps-0 text-nowrap overflow-hidden">
                  <button
                    type="button"
                    className="btn btn-link text-black p-0 lh-sm me-2"
                    title="Remove Welder"
                    onClick={() => handleRemoveWelder(welder)}
                    disabled={data.locked || areAnyTestsCompleted(data, welder)}
                    hidden={role === AccessLevel.GeneralUser}
                  >
                    <FontAwesomeIcon role="button" icon={faXmark} border={true} />
                  </button>
                  <span title={welder.badgeNumber}>{welder.badgeNumber}</span>
                </td>
                {data.tests.map((test) => (
                  <td key={test.recordId} className="text-center">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={doesWelderHaveTest(data, welder, test)}
                      onChange={(e) => handleTestSelectionChanged(test, welder, e.currentTarget.checked)}
                      disabled={data.locked || role === AccessLevel.GeneralUser || isTestCompleted(data, welder, test)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p>Click the search button to add welders.</p>
      )}
    </div>
  );
}
